import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import { useToast } from '../context/ToastContext'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

const STATUS_STYLES = {
  pending:  { bg: '#FFF8E6', color: AMBER },
  approved: { bg: '#EFF6FF', color: BLUE },
  paid:     { bg: '#F0FDF4', color: GREEN },
  rejected: { bg: '#FEF2F2', color: RED },
}

function AdminReferrals() {
  const { showToast } = useToast()
  const [referrals, setReferrals]   = useState([])
  const [loading, setLoading]       = useState(true)
  const [filter, setFilter]         = useState('all')
  const [search, setSearch]         = useState('')
  const [processing, setProcessing] = useState({})
  const [payoutRef, setPayoutRef]   = useState({})
  const [stats, setStats]           = useState({ total: 0, pending: 0, approved: 0, paid: 0, totalBDT: 0, totalUSD: 0 })

  useEffect(() => { fetchReferrals() }, [filter]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReferrals = async () => {
    setLoading(true)
    let query = supabase.from('referrals')
      .select(`
        *,
        referrer:referrer_seller_id(shop_name, seller_currency, payout_wallet_type, payout_wallet_number, phone),
        referee:referee_seller_id(shop_name, seller_currency, created_at)
      `)
      .order('created_at', { ascending: false })

    if (filter !== 'all') query = query.eq('status', filter)

    const { data } = await query
    const rows = data || []
    setReferrals(rows)

    // Stats
    const all = (await supabase.from('referrals').select('status, discount_amount, currency')).data || []
    setStats({
      total:    all.length,
      pending:  all.filter(r => r.status === 'pending').length,
      approved: all.filter(r => r.status === 'approved').length,
      paid:     all.filter(r => r.status === 'paid').length,
      totalBDT: all.filter(r => r.currency === 'BDT' && r.status === 'paid').reduce((s, r) => s + Number(r.discount_amount || 0), 0),
      totalUSD: all.filter(r => r.currency === 'USD' && r.status === 'paid').reduce((s, r) => s + Number(r.discount_amount || 0), 0),
    })
    setLoading(false)
  }

  const updateStatus = async (id, status, ref) => {
    setProcessing(p => ({ ...p, [id]: true }))
    await supabase.from('referrals').update({
      status,
      paid_at: status === 'paid' ? new Date().toISOString() : null,
      payout_reference: ref || null,
    }).eq('id', id)
    showToast('Referral ' + status)
    fetchReferrals()
    setProcessing(p => ({ ...p, [id]: false }))
  }

  const filtered = referrals.filter(r => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      r.referrer?.shop_name?.toLowerCase().includes(q) ||
      r.referee?.shop_name?.toLowerCase().includes(q) ||
      r.promo_code?.toLowerCase().includes(q)
    )
  })

  const inp = { border: '1px solid ' + BORDER, borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, system-ui, sans-serif', boxSizing: 'border-box' }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
      <div style={{ padding: '24px 32px' }}>

        {/* Header */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Referral Disbursements</div>
          <div style={{ fontSize: '13px', color: MUTED, marginTop: '3px' }}>Track all referrals, approve rewards and mark payouts as paid</div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {[
            { label: 'Total referrals', value: stats.total,    color: TEXT },
            { label: 'Pending',         value: stats.pending,  color: AMBER },
            { label: 'Approved',        value: stats.approved, color: BLUE },
            { label: 'Paid out',        value: stats.paid,     color: GREEN },
            { label: 'BDT paid',        value: '৳' + stats.totalBDT.toLocaleString(), color: GREEN },
            { label: 'USD paid',        value: '$' + stats.totalUSD, color: GREEN },
          ].map(s => (
            <div key={s.label} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, padding: '14px 16px' }}>
              <div style={{ fontSize: '22px', fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '3px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '4px', padding: '4px', background: WHITE, borderRadius: '10px', border: '1px solid ' + BORDER }}>
            {['all', 'pending', 'approved', 'paid', 'rejected'].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                style={{ padding: '6px 14px', borderRadius: '7px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: filter === s ? BLUE : 'transparent', color: filter === s ? WHITE : MUTED, fontFamily: 'Space Grotesk, system-ui, sans-serif', transition: 'all 0.12s' }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by seller name or code..."
            style={{ ...inp, flex: 1, minWidth: '200px' }}
            onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
        </div>

        {/* Table */}
        <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: MUTED }}>Loading referrals...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>🎁</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: TEXT }}>No referrals found</div>
              <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>Referrals appear here when sellers use referral codes during signup</div>
            </div>
          ) : filtered.map((ref, i) => {
            const sc = STATUS_STYLES[ref.status] || STATUS_STYLES.pending
            const isProcessing = processing[ref.id]
            return (
              <div key={ref.id} style={{ padding: '16px 20px', borderBottom: i < filtered.length - 1 ? '1px solid #F5F5F5' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>

                  {/* Referral chain */}
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{ref.referrer?.shop_name || 'Unknown'}</span>
                      <span style={{ fontSize: '11px', color: MUTED }}>referred</span>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: BLUE }}>{ref.referee?.shop_name || 'Unknown'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', background: BG, padding: '2px 8px', borderRadius: '6px', color: TEXT }}>{ref.promo_code}</span>
                      <span style={{ fontSize: '11px', color: MUTED }}>{new Date(ref.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      {ref.plan_type && <span style={{ fontSize: '11px', color: MUTED }}>· {ref.plan_type}</span>}
                    </div>
                    {/* Payout wallet */}
                    {ref.referrer?.payout_wallet_type && (
                      <div style={{ marginTop: '6px', fontSize: '11.5px', color: MUTED }}>
                        Payout to: <strong style={{ color: TEXT }}>{ref.referrer.payout_wallet_type}</strong> {ref.referrer.payout_wallet_number}
                      </div>
                    )}
                    {ref.paid_at && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: GREEN }}>
                        Paid {new Date(ref.paid_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {ref.payout_reference && <span style={{ marginLeft: '6px', fontFamily: 'JetBrains Mono, monospace' }}>· {ref.payout_reference}</span>}
                      </div>
                    )}
                  </div>

                  {/* Amount + status */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                    {ref.discount_amount && (
                      <div style={{ fontSize: '18px', fontWeight: 800, color: BLUE }}>
                        {ref.currency === 'BDT' ? '৳' : '$'}{ref.discount_amount}
                        <span style={{ fontSize: '11px', fontWeight: 400, color: MUTED, marginLeft: '4px' }}>each</span>
                      </div>
                    )}
                    <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', background: sc.bg, color: sc.color }}>
                      {ref.status.charAt(0).toUpperCase() + ref.status.slice(1)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0, minWidth: '180px' }}>
                    {ref.status === 'pending' && (
                      <>
                        <button onClick={() => updateStatus(ref.id, 'approved')} disabled={isProcessing}
                          style={{ padding: '8px 16px', borderRadius: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', fontSize: '12px', fontWeight: 700, color: BLUE, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          {isProcessing ? '...' : '✓ Approve'}
                        </button>
                        <button onClick={() => updateStatus(ref.id, 'rejected')} disabled={isProcessing}
                          style={{ padding: '8px 16px', borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12px', fontWeight: 700, color: RED, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          Reject
                        </button>
                      </>
                    )}
                    {ref.status === 'approved' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <input type="text" placeholder="Payout ref (bKash/bank)"
                          value={payoutRef[ref.id] || ''}
                          onChange={e => setPayoutRef(p => ({ ...p, [ref.id]: e.target.value }))}
                          style={{ ...inp, fontSize: '11.5px' }}
                          onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                        <button onClick={() => updateStatus(ref.id, 'paid', payoutRef[ref.id])} disabled={isProcessing}
                          style={{ padding: '8px 16px', borderRadius: '8px', background: GREEN, border: 'none', fontSize: '12px', fontWeight: 700, color: WHITE, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          {isProcessing ? '...' : '✓ Mark as paid'}
                        </button>
                      </div>
                    )}
                    {ref.status === 'paid' && (
                      <div style={{ fontSize: '12px', color: GREEN, fontWeight: 600 }}>✓ Disbursed</div>
                    )}
                    {ref.status === 'rejected' && (
                      <button onClick={() => updateStatus(ref.id, 'pending')}
                        style={{ padding: '8px 16px', borderRadius: '8px', background: BG, border: '1px solid ' + BORDER, fontSize: '12px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      </div>
    </div>
  )
}

export default AdminReferrals